import type {
  AiCreditPackOption,
  DeployedAppRow,
  SharedCreditUsageRow,
  UsageResourceTileConfig,
} from "./types";

// Mocked lower-section data until the deployed-apps usage backend is available.
export const MOCK_DEPLOYED_APPS_ROWS: DeployedAppRow[] = [
  {
    id: "customer-dashboard",
    title: "Customer Dashboard",
    creator: "Alice Chen",
    lastModified: "Mar 5, 2026",
  },
  {
    id: "inventory-manager",
    title: "Inventory Manager",
    creator: "Bob Martinez",
    lastModified: "Mar 3, 2026",
  },
  {
    id: "sales-pipeline",
    title: "Sales Pipeline",
    creator: "Carol Johnson",
    lastModified: "Feb 28, 2026",
  },
];

// Mocked credit pool usage rows until shared-credit reporting endpoints land.
export const MOCK_SHARED_CREDIT_USAGE_ROWS: SharedCreditUsageRow[] = [
  {
    id: "usage-1",
    date: "Mar 6, 2026",
    user: "alice@superblocks.clark",
    creditsUsed: 42,
    sourceLabel: "User Allocation",
    sourceTone: "allocation",
  },
  {
    id: "usage-2",
    date: "Mar 6, 2026",
    user: "alice@superblocks.clark",
    creditsUsed: 18,
    sourceLabel: "Org Credit Pool",
    sourceTone: "pool",
  },
  {
    id: "usage-3",
    date: "Mar 6, 2026",
    user: "bob@superblocks.clark",
    creditsUsed: 31,
    sourceLabel: "User Allocation",
    sourceTone: "allocation",
  },
  {
    id: "usage-4",
    date: "Mar 5, 2026",
    user: "carol@superblocks.clark",
    creditsUsed: 12,
    sourceLabel: "Org Credit Pool",
    sourceTone: "pool",
  },
  {
    id: "usage-5",
    date: "Mar 5, 2026",
    user: "alice@superblocks.clark",
    creditsUsed: 55,
    sourceLabel: "User Allocation",
    sourceTone: "allocation",
  },
  {
    id: "usage-6",
    date: "Mar 4, 2026",
    user: "dave@superblocks.clark",
    creditsUsed: 8,
    sourceLabel: "User Allocation",
    sourceTone: "allocation",
  },
  {
    id: "usage-7",
    date: "Mar 4, 2026",
    user: "bob@superblocks.clark",
    creditsUsed: 27,
    sourceLabel: "Org Credit Pool",
    sourceTone: "pool",
  },
  {
    id: "usage-8",
    date: "Mar 4, 2026",
    user: "carol@superblocks.clark",
    creditsUsed: 35,
    sourceLabel: "User Allocation",
    sourceTone: "allocation",
  },
  {
    id: "usage-9",
    date: "Mar 3, 2026",
    user: "eve@superblocks.clark",
    creditsUsed: 19,
    sourceLabel: "User Allocation",
    sourceTone: "allocation",
  },
  {
    id: "usage-10",
    date: "Mar 3, 2026",
    user: "dave@superblocks.clark",
    creditsUsed: 14,
    sourceLabel: "Org Credit Pool",
    sourceTone: "pool",
  },
];

// Mocked shared-credit summary values until the usage summary API is available.
export const MOCK_SHARED_CREDITS_TOTAL = 500;
export const MOCK_SHARED_CREDITS_USED = 140;

// The builder-seat assignment API does not yet expose total purchased seats for the tile.
export const MOCK_BUILDER_SEAT_TILE_TOTAL = 5;

// Manage-plan builder pricing and purchased-seat counts are mocked until billing plan-summary APIs land.
export const MOCK_MANAGE_PLAN_BUILDER_SEAT_TOTAL = 5;
export const MOCK_MANAGE_PLAN_BUILDER_SEAT_USED = 5;
/** Monthly plan: $100/seat/month. Annual plan: $80/seat/month ($960/yr). */
export const MOCK_MANAGE_PLAN_BUILDER_SEAT_PRICE_MONTHLY_USD = 100;
export const MOCK_MANAGE_PLAN_BUILDER_SEAT_PRICE_ANNUAL_PER_MONTH_USD = 80;

// Prices are intentionally hardcoded for now, matching the current Stripe price list.
export const AI_CREDIT_PACK_OPTIONS: AiCreditPackOption[] = [
  {
    id: "monthly-100",
    credits: 100,
    priceUsd: 125,
    billingInterval: "monthly",
  },
  {
    id: "monthly-250",
    credits: 250,
    priceUsd: 313,
    billingInterval: "monthly",
  },
  {
    id: "monthly-500",
    credits: 500,
    priceUsd: 625,
    billingInterval: "monthly",
  },
  {
    id: "monthly-1000",
    credits: 1000,
    priceUsd: 1250,
    billingInterval: "monthly",
  },
  {
    id: "monthly-2500",
    credits: 2500,
    priceUsd: 3125,
    billingInterval: "monthly",
  },
  {
    id: "annual-100",
    credits: 100,
    priceUsd: 1200,
    billingInterval: "annual",
  },
  {
    id: "annual-250",
    credits: 250,
    priceUsd: 3000,
    billingInterval: "annual",
  },
  {
    id: "annual-500",
    credits: 500,
    priceUsd: 6000,
    billingInterval: "annual",
  },
  {
    id: "annual-1000",
    credits: 1000,
    priceUsd: 12000,
    billingInterval: "annual",
  },
  {
    id: "annual-2500",
    credits: 2500,
    priceUsd: 30000,
    billingInterval: "annual",
  },
];

export const USAGE_RESOURCE_TILES: UsageResourceTileConfig[] = [
  {
    key: "ai_builders",
    title: "Assigned AI Builders",
    subtitle: "Builder licenses assigned",
    accentClassName:
      "border-[#0099ff] bg-white shadow-[0_0_0_4px_rgba(0,153,255,0.12),0_0_0_1px_#0099ff]",
    progressClassName: "bg-sky-500",
  },
  {
    key: "deployed_apps",
    title: "Deployed Apps",
    subtitle: "Apps currently deployed",
    accentClassName:
      "border-[#0099ff] bg-white shadow-[0_0_0_4px_rgba(0,153,255,0.12),0_0_0_1px_#0099ff]",
    progressClassName: "bg-sky-500",
    mocked: true,
  },
  {
    key: "shared_ai_credits",
    title: "Shared AI Credits",
    subtitle: "Credits used from shared pool",
    accentClassName:
      "border-[#0099ff] bg-white shadow-[0_0_0_4px_rgba(0,153,255,0.12),0_0_0_1px_#0099ff]",
    progressClassName: "bg-sky-500",
    mocked: true,
  },
];
